/**
 * ⌘K — search everything.
 *
 * One endpoint behind it, deliberately: permission gating and blind-tasting
 * redaction live in exactly one place on the server. This component is only ever
 * a view onto whatever the server decided the member is allowed to see.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SearchHit, SearchType } from '../api/search';
import { searchApi } from '../api/search';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

const TYPE_LABEL: Record<SearchType, string> = {
  event: 'Tastings',
  wine: 'Wines',
  member: 'Members',
  producer: 'Producers',
  region: 'Regions',
};

const TYPE_ORDER: SearchType[] = ['event', 'wine', 'member', 'producer', 'region'];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const debounced = useDebouncedValue(q, 200);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchApi.search(debounced),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const hits: SearchHit[] = data?.results ?? [];

  // ⌘K / Ctrl-K anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setCursor(0);
      // Focus after paint, or the input isn't in the DOM yet.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQ('');
    }
  }, [open]);

  useEffect(() => setCursor(0), [debounced]);

  function go(hit: SearchHit) {
    setOpen(false);
    navigate(hit.href);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && hits[cursor]) {
      e.preventDefault();
      go(hits[cursor]);
    }
  }

  if (!open) return null;

  // Group for display, but keep the flat index for keyboard navigation — the two
  // must agree or arrow keys select a different row than the one that's highlighted.
  const grouped = TYPE_ORDER.map((type) => ({
    type,
    items: hits
      .map((h, i) => ({ hit: h, index: i }))
      .filter(({ hit }) => hit.type === type),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-ink-950/70 p-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search the club"
        className="w-full max-w-xl overflow-hidden rounded-xl border border-ink-800 bg-ink-900 shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-ink-800 px-4">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="shrink-0 text-cream-300/50"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search tastings, wines, members, producers…"
            className="w-full bg-transparent py-4 text-cream-50 placeholder:text-cream-300/40 focus:outline-none"
            aria-label="Search"
          />
          {isFetching ? <span className="text-xs text-cream-300/40">…</span> : null}
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          {debounced.trim().length < 2 ? (
            <p className="px-4 py-8 text-center text-sm text-cream-300/45">
              Four decades of tastings, wines, and members.
            </p>
          ) : !hits.length && !isFetching ? (
            <p className="px-4 py-8 text-center text-sm text-cream-300/45">
              Nothing found for “{debounced}”.
            </p>
          ) : (
            grouped.map((group) => (
              <div key={group.type}>
                <p className="px-4 pb-1 pt-3 text-[11px] uppercase tracking-wide text-cream-300/40">
                  {TYPE_LABEL[group.type]}
                </p>
                <ul>
                  {group.items.map(({ hit, index }) => (
                    <li key={`${hit.type}-${hit.id}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setCursor(index)}
                        onClick={() => go(hit)}
                        className={`flex w-full items-baseline justify-between gap-3 px-4 py-2.5 text-left transition ${
                          index === cursor ? 'bg-ink-800' : ''
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-cream-100">{hit.title}</span>
                          {hit.subtitle ? (
                            <span className="block truncate text-xs text-cream-300/55">
                              {hit.subtitle}
                            </span>
                          ) : null}
                        </span>
                        {hit.year ? (
                          <span className="shrink-0 text-xs text-cream-300/40">{hit.year}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-ink-800 px-4 py-2 text-[11px] text-cream-300/40">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
